import { Drawer, DrawerBody, DrawerHeader, DrawerHeading, DrawerProps, Id } from "@dgs/core";
import { FC, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { SelfTravelDrawerContent } from "../../../../../../shared/SelfTravelDrawer";
import { TravelInquiryDrawerContent } from "../../../../../../shared/TravelInquiryDrawer";
import { GuestSelfTravelResource } from "../../../../../../shared/types/selfTravel";
import { GuestTravelInquiry } from "../../../../../../shared/types/travelInquiry";
import { TravelSectionSettingsResource } from "../../../../shared/types";
import {
	SelectableSegmentType,
	SelectableTravelSegmentIndexResource,
	TravelDirection,
	TravelSegmentFormValues,
	TravelSegmentIndexResource,
} from "../../../../shared/types/travelSegment";
import { TravelTemplateIndexResource } from "../../../../shared/types/travelTemplate";
import { AddTravelSegmentDrawerContent } from "./AddTravelSegmentDrawerContent";
import { AddTravelTemplateDrawerContent } from "./AddTravelTemplateDrawerContent";
import { AlternativeTravelPlanningChoiceButtons } from "./AlternativeTravelPlanningChoiceButtons";
import { CreateTravelSegmentChoiceButton } from "./CreateTravelSegmentChoiceButton";

interface Props extends DrawerProps {
	registrationFormId: Id;
	heading: string;
	direction: TravelDirection;
	initialSelection: (TravelSegmentIndexResource | TravelSegmentFormValues)[];
	inquiry: GuestTravelInquiry | null;
	selfTravel: GuestSelfTravelResource | null;
	onAddTravelSegments: (selection: (SelectableTravelSegmentIndexResource | TravelSegmentFormValues)[]) => void;
	onAddTravelInquiry: (inquiry: GuestTravelInquiry) => void;
	onAddSelfTravel: (selfTravel: GuestSelfTravelResource) => void;
	onSelectTravelTemplate: (travelTemplate: TravelTemplateIndexResource) => void;
	settings: TravelSectionSettingsResource;
}

const WideDrawer = styled(Drawer)`
	width: 780px;
	max-width: 100vw;
`;

interface DrawerContentProps {
	registrationFormId: Id;
	direction: TravelDirection;
	initialSelection: (TravelSegmentIndexResource | TravelSegmentFormValues)[];
	inquiry: GuestTravelInquiry | null;
	selfTravel: GuestSelfTravelResource | null;
	onAddTravelSegments: (selection: (SelectableTravelSegmentIndexResource | TravelSegmentFormValues)[]) => void;
	onAddTravelInquiry: (inquiry: GuestTravelInquiry) => void;
	onAddSelfTravel: (selfTravel: GuestSelfTravelResource) => void;
	onSelectTravelTemplate: (travelTemplate: TravelTemplateIndexResource) => void;
	settings: TravelSectionSettingsResource;
}

const allFilterableTypes: SelectableSegmentType[] = ["flight", "train", "car", "shuttle"];

type DrawerSelection = "self-travel" | "inquiry";

const WideDrawerBody = styled(DrawerBody)`
	flex: 1;
`;

const DrawerContent: FC<DrawerContentProps> = ({
	direction,
	initialSelection,
	onAddTravelSegments,
	onSelectTravelTemplate,
	registrationFormId,
	inquiry,
	selfTravel,
	onAddTravelInquiry,
	onAddSelfTravel,
	settings,
}) => {
	const { t } = useTranslation();
	const filterableAvailableTypes = useMemo(() => {
		return allFilterableTypes.filter((type) => settings.availableTravelSegmentTypes.includes(type));
	}, [settings.availableTravelSegmentTypes]);
	const [travelPlanning, setTravelPlanning] = useState<DrawerSelection | null>(() => {
		if (!settings.canSelectTravelSegments && !settings.canSelectTravelTemplates) {
			if (settings.canSelfTravel && !settings.canCreateTravelInquiries) {
				return "self-travel";
			} else if (settings.canCreateTravelInquiries && !settings.canSelfTravel) {
				return "inquiry";
			}
		}
		return null;
	});

	if (travelPlanning == "self-travel") {
		return (
			<SelfTravelDrawerContent
				selfTravel={selfTravel}
				onSubmit={onAddSelfTravel}
				isRemarksFieldRequired={settings.isRemarksFieldRequired}
				direction={direction}
			/>
		);
	}
	if (travelPlanning == "inquiry") {
		return <TravelInquiryDrawerContent inquiry={inquiry} onSubmit={onAddTravelInquiry} />;
	}

	if (settings.canSelectTravelSegments) {
		return (
			<AddTravelSegmentDrawerContent
				registrationFormId={registrationFormId}
				availableTypes={filterableAvailableTypes}
				direction={direction}
				initialSelection={initialSelection}
				onSave={onAddTravelSegments}
				settings={settings}
			>
				{(addToSelection) => (
					<AlternativeTravelPlanningChoiceButtons
						title={t("Further options")}
						canCreateTravelInquiries={settings.canCreateTravelInquiries}
						canSelfTravel={settings.canSelfTravel}
						onInquiry={() => setTravelPlanning("inquiry")}
						onSelfTravel={() => setTravelPlanning("self-travel")}
						travelSegmentButton={
							settings.canCreateTravelSegments ? (
								<CreateTravelSegmentChoiceButton
									availableTypes={settings.availableTravelSegmentTypes}
									direction={direction}
									onCreateTravelSegment={addToSelection}
								/>
							) : undefined
						}
					/>
				)}
			</AddTravelSegmentDrawerContent>
		);
	} else if (settings.canSelectTravelTemplates) {
		return (
			<AddTravelTemplateDrawerContent direction={direction} onSelect={onSelectTravelTemplate}>
				<AlternativeTravelPlanningChoiceButtons
					title={t("Further options")}
					canCreateTravelInquiries={settings.canCreateTravelInquiries}
					canSelfTravel={settings.canSelfTravel}
					onInquiry={() => setTravelPlanning("inquiry")}
					onSelfTravel={() => setTravelPlanning("self-travel")}
				/>
			</AddTravelTemplateDrawerContent>
		);
	} else {
		return (
			<WideDrawerBody>
				<AlternativeTravelPlanningChoiceButtons
					canCreateTravelInquiries={settings.canCreateTravelInquiries}
					canSelfTravel={settings.canSelfTravel}
					onInquiry={() => setTravelPlanning("inquiry")}
					onSelfTravel={() => setTravelPlanning("self-travel")}
				/>
			</WideDrawerBody>
		);
	}
};

export const TravelDrawer: FC<Props> = ({
	registrationFormId,
	heading,
	direction,
	initialSelection,
	inquiry,
	selfTravel,
	onAddTravelSegments,
	onAddTravelInquiry,
	onSelectTravelTemplate,
	onAddSelfTravel,
	onClose,
	settings,
	...drawerProps
}) => {
	const closeAfterAction =
		<T,>(cb: (item: T) => void) =>
		(item: T) => {
			cb(item);
			onClose?.();
		};

	return (
		<WideDrawer {...drawerProps} onClose={onClose}>
			<DrawerHeader onClose={onClose}>
				<DrawerHeading>{heading}</DrawerHeading>
			</DrawerHeader>
			<DrawerContent
				registrationFormId={registrationFormId}
				direction={direction}
				initialSelection={initialSelection}
				inquiry={inquiry}
				selfTravel={selfTravel}
				onAddTravelSegments={closeAfterAction(onAddTravelSegments)}
				onAddTravelInquiry={closeAfterAction(onAddTravelInquiry)}
				onAddSelfTravel={closeAfterAction(onAddSelfTravel)}
				onSelectTravelTemplate={closeAfterAction(onSelectTravelTemplate)}
				settings={settings}
			/>
		</WideDrawer>
	);
};
